import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { Modal } from "antd";

import { EItemFormatType, EItemType, IItemOption } from "../../interfaces";

import cnpj from "../../utils/masks/cnpj";
import cpf from "../../utils/masks/cpf";
import currency from "../../utils/masks/currency";
import phone from "../../utils/masks/phone";
import cellphoneValidator from "../../utils/validators/cellphoneValidator";
import cnpjValidator from "../../utils/validators/cnpjValidator";
import cpfValidator from "../../utils/validators/cpfValidator";
import emailValidator from "../../utils/validators/emailValidator";
import currencyValidator from "../../utils/validators/currencyValidator";
import { IInputProps } from ".";

const REQUIRED_FIELD_MESSAGE = 'Campo obrigatório.';

type Errors = { [key: string]: { message: string } }

type ValidationResult = {
  unformattedValues: { [key: string]: string | number };
  errors: Errors;
};

function validateFields(
  values: { [key: string]: string },
  fields: {
    name: string;
    format?: EItemFormatType;
    currency_min_value?: number | null;
    currency_max_value?: number | null;
  }[],
  options?: {
    validateOnlyWhenComplete?: boolean;
    forceValidate?: boolean;
  }
): ValidationResult {
  let errors: ValidationResult['errors'] = {};
  let unformattedValues: ValidationResult['unformattedValues'] = {};

  const entries = Object.entries(values);

  // Campos obrigatórios ausentes
  const errorFields = fields.filter((field) => {
    const item = entries.find(([name]) => name === field.name);
    return !item || !item[1];
  });

  errorFields.forEach((field) => {
    errors[field.name] = { message: 'Campo obrigatório.' };
  });

  function insertError(fieldName: string, message: string) {
    if (errors[fieldName]?.message === REQUIRED_FIELD_MESSAGE) {
      return;
    }

    errors[fieldName] = { message };
  }

  for (const [field, value] of entries) {
    const format = fields.find((f) => f.name === field)?.format;

    let fieldValue: string | number = value;

    if (format === EItemFormatType.CNPJ) {
      const canValidate = (options?.forceValidate || !options?.validateOnlyWhenComplete || value.length === 18);

      if (canValidate && !cnpjValidator.validate(value)) {
        insertError(field, 'CNPJ inválido.');
        continue;
      }
      fieldValue = cnpj.unMask(value);
    }

    if (format === EItemFormatType.CPF) {
      const cpfValue = cpf.unMask(value);

      const canValidate = options?.forceValidate || !options?.validateOnlyWhenComplete || value.length === 14;

      if (canValidate && !cpfValidator.validate(cpfValue)) {
        insertError(field, 'CPF inválido.');
        continue;
      }
      fieldValue = cpfValue;
    }

    if (format === EItemFormatType.CELLPHONE) {
      const canValidate = options?.forceValidate || !options?.validateOnlyWhenComplete || value.length >= 14;

      if (canValidate && !cellphoneValidator.validate(value)) {
        insertError(field, 'Telefone inválido.');
        continue;
      }
      fieldValue = phone.unMask(value);
    }

    if (format === EItemFormatType.EMAIL) {
      const canValidate = options?.forceValidate || !options?.validateOnlyWhenComplete;

      if (canValidate && !emailValidator.validate(value)) {
        insertError(field, 'Email inválido.');
        continue;
      }
    }

    if (format === EItemFormatType.CURRENCY) {
      const currencyValue = currency.unMask(value);

      const currency_min_value = fields.find((f) => f.name === field)?.currency_min_value;
      const currency_max_value = fields.find((f) => f.name === field)?.currency_max_value;

      const canValidate = options?.forceValidate || !options?.validateOnlyWhenComplete;

      if (canValidate && !currencyValidator.validate(currencyValue, currency_min_value, currency_max_value)) {
        insertError(field, currencyValidator.getErrorMessage(currency_min_value, currency_max_value));
        continue;
      }

      fieldValue = currencyValue;
    }

    unformattedValues[field] = fieldValue;
  }

  return { errors, unformattedValues };
}

export default function useInput({
  currentItem,
  onSend,
  isLoading,
  onReset,
}: Omit<IInputProps, "showResetButton">) {
  const [values, setValues] = useState({});
  const [selectedOptions, setSelectedOptions] = useState<IItemOption[]>([]);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const isResponse = useMemo(() => {
    return !!currentItem?.response;
  }, [currentItem]);

  const isFinished = useMemo(() => {
    return currentItem?.type && [EItemType.MESSAGE, EItemType.RESULT].includes(currentItem?.type) && !currentItem?.next;
  }, [currentItem]);

  const isCollect = useMemo(() => {
    return !isResponse && currentItem?.type === EItemType.COLLECT;
  }, [isResponse, currentItem]);

  const isInput = useMemo(() => {
    return !isResponse && currentItem?.type === EItemType.FIELD;
  }, [isResponse, currentItem]);

  const resetValuesAfterSend = useCallback((success: boolean) => {
    if (!success) {
      return;
    }

    setValues({});
    setSelectedOptions([]);
    setErrors({});
  }, []);

  const showWrittingStatus = useMemo(() => {
    return (isLoading || (!!currentItem && [EItemType.MESSAGE, EItemType.RESULT].includes(currentItem?.type)))
  }, [isLoading, currentItem]);

  const handleSend = async () => {
    if (!currentItem) {
      return;
    }

    setIsSubmiting(true);

    if (currentItem.type === EItemType.COLLECT) {
      const { errors: validationErrors, unformattedValues } = validateFields(values, currentItem.fields ?? []);

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setIsSubmiting(false);
        return;
      }

      const fieldsWithData = Object.entries(unformattedValues).map(([name, value]) => {
        const currentField = currentItem?.fields?.find((e) => e.name === name);
        return {
          name,
          value,
          format: currentField?.format,
          label: currentField?.label as string,
        }
      })

      const success = await onSend({
        item: currentItem,
        collectResponse: fieldsWithData,
      });

      resetValuesAfterSend(success);
    }

    if (currentItem.type === EItemType.OPTIONS) {
      if (selectedOptions.length) {
        const success = await onSend({
          item: currentItem,
          optionResponse: selectedOptions,
        });

        resetValuesAfterSend(success);
      } else {
        Modal.warning({
          title: 'Selecione uma das opções para continuar.',
          okButtonProps: {
            style: {
              backgroundColor: '#2D66A2',
              borderColor: '#2D66A2'
            }
          },
          style: {
            fontFamily: 'DM Sans',
          },
        });
      }
    }

    if (currentItem.type === EItemType.FIELD) {
      let value: string | number = values?.[currentItem.id as keyof typeof values];

      const { errors: validationErrors, unformattedValues } = validateFields(
        { [currentItem.id]: value },
        [{
          name: currentItem.id,
          format: currentItem.format,
          currency_min_value: currentItem.currency_min_value,
          currency_max_value: currentItem.currency_max_value
        }]
      );

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setIsSubmiting(false);
        return;
      }

      const success = await onSend({
        item: currentItem,
        singleResponse: unformattedValues[currentItem.id],
      });

      resetValuesAfterSend(success);
    }

    setIsSubmiting(false);
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>, format?: EItemFormatType, currency_min_value?: number | null, currency_max_value?: number | null, options?: { forceValidate?: boolean }) => {
    let value = e.target.value;

    const fieldFormat = format || currentItem?.format;

    if (fieldFormat === EItemFormatType.CURRENCY) {
      value = currency.mask(value);
    }

    if (fieldFormat === EItemFormatType.CNPJ) {
      value = cnpj.mask(value);
    }

    if (fieldFormat === EItemFormatType.CELLPHONE) {
      value = phone.mask(value);
    }

    if (fieldFormat === EItemFormatType.CPF) {
      value = cpf.mask(value);
    }

    setValues((prevState) => {
      const updatedValues = {
        ...prevState,
        [e.target.name]: value,
      }

      const { errors } = validateFields(updatedValues, [{
        name: e.target.name,
        format: fieldFormat,
        currency_min_value: currency_min_value || currentItem?.currency_min_value,
        currency_max_value: currency_max_value || currentItem?.currency_max_value
      }], { validateOnlyWhenComplete: true, forceValidate: options?.forceValidate });

      if (!errors[e.target.name]) {
        setErrors((prevState) => {
          const newErrors = { ...prevState };

          delete newErrors[e.target.name];

          return newErrors;
        })
      }

      setErrors((prevState) => ({ ...prevState, ...errors }));

      return updatedValues;
    });
  }

  const handleClickOption = (value: IItemOption) => {
    if (!currentItem?.allow_multiple_options) {
      setSelectedOptions([value]);

      return;
    }
    setSelectedOptions((prevState => {
      if (prevState?.find((e) => e.text === value.text)) {
        return prevState.filter((e) => e.text !== value.text);
      }

      return prevState?.concat(value) || [];
    }));
  }

  const handleReset = useCallback(() => {
    Modal.warning({
      title: 'Deseja mesmo começar sua resposta do zero?',
      content: 'Suas respostas atuais serão removidas e não poderão ser recuperadas.',
      cancelText: "Cancelar",
      okText: "Sim",
      okType: "danger",
      okCancel: true,
      onCancel: () => { },
      onOk: onReset,
      style: {
        fontFamily: 'DM Sans'
      }
    })
  }, [onReset]);

  return {
    isCollect,
    isFinished,
    isInput,
    handleSend,
    handleChange,
    isResponse,
    values,
    errors,
    selectedOptions,
    handleClickOption,
    isSubmiting,
    showWrittingStatus,
    handleReset,
  }
}

